import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { GraduationCap, Code, Download, Award } from 'lucide-react';
import AnimatedBackground from './AnimatedBackground';

// Data for the new Education section
const education = [
    {
        degree: "Secondary School (10th)",
        institution: "St. Antony’s Public School",
        years: "2019 – 2020",
        score: "Percentage: 82.4%",
        icon: Award,
        color: "#00ff41"
    },
    {
        degree: "Higher Secondary (PCMS)",
        institution: "Vidyaniketan PU Science College",
        years: "2020 – 2022",
        score: "Grade: 89.5%",
        icon: Award,
        color: "#00ffff"
    },
    {
        degree: "B.E in Computer Science",
        institution: "KLE Technological University, Hubli",
        years: "2022 – 2026",
        score: "CGPA: 7.49 / 10",
        icon: GraduationCap,
        color: "#8b00ff"
    }
];

// Academic Journey timeline data
const timeline = [
    {
        year: "2022",
        title: "Started Computer Science Journey",
        description: "Began my B.E. in Computer Science at KLE Technological University, focusing on core programming concepts.",
        icon: GraduationCap,
        color: "#00ff41",
    },
    {
        year: "2023",
        title: "Deep Dive into Development",
        description: "Mastered data structures, algorithms, and full-stack development, building multiple hands-on projects.",
        icon: Code,
        color: "#00ffff",
    },
    {
        year: "2024",
        title: "Advanced Technologies & AI",
        description: "Expanded into machine learning and computer vision, developing a forest fire detection system with YOLOv11.",
        icon: GraduationCap,
        color: "#ff0080",
    },
    {
        year: "2025",
        title: "Full-Stack Mastery & Innovation",
        description: "Honed my skills with modern frameworks and cloud tech to create and deploy comprehensive web applications.",
        icon: Code,
        color: "#ffff00",
    }
];

export default function About() {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-100px" });

    return (
        <section id="about" ref={ref} className="relative py-20 min-h-screen bg-gradient-to-b from-gray-950 via-black to-black text-white overflow-hidden">
            {/* Animated Background */}
            <AnimatedBackground variant="waves" intensity="low" />
            <AnimatedBackground variant="stars" intensity="low" />
            
            {/* Different background pattern - diagonal lines instead of grid */}
            <div className="absolute inset-0 opacity-[0.03]">
                <div
                    className="w-full h-full"
                    style={{
                        backgroundImage: `
                            repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(0, 255, 65, 0.1) 35px, rgba(0, 255, 65, 0.1) 70px)
                        `,
                    }}
                />
            </div>
            
            <div className="container mx-auto px-6 relative z-10">
                {/* Section Header */}
                <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 1 }}
                    className="text-center mb-16"
                >
                    <h2 className="text-5xl md:text-6xl font-display font-black mb-4">
                        <span className="glitch-text text-neon-pink neon-glow" data-text="ABOUT ME">
                            ABOUT ME
                        </span>
                    </h2>
                    <div className="w-24 h-1 bg-gradient-to-r from-neon-pink to-neon-blue mx-auto" />
                </motion.div>

                {/* Part 1: Bio Section - Card-based layout */}
                <motion.div
                    initial={{ opacity: 0, y: 50, scale: 0.95 }}
                    animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
                    transition={{ duration: 0.8, delay: 0.2, type: 'spring', stiffness: 100 }}
                    className="max-w-5xl mx-auto mb-24"
                    whileHover={{ y: -5 }}
                >
                    <motion.div 
                        className="bg-gradient-to-br from-gray-900/80 to-gray-950/80 backdrop-blur-sm rounded-2xl border border-gray-800/50 p-8 md:p-12 shadow-2xl"
                        whileHover={{ 
                            boxShadow: '0 0 40px rgba(0, 255, 65, 0.2)',
                            borderColor: 'rgba(0, 255, 65, 0.3)'
                        }}
                        transition={{ duration: 0.3 }}
                    >
                        <div className="space-y-6">
                            {/* Bio Content */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={isInView ? { opacity: 1, y: 0 } : {}}
                                transition={{ duration: 0.8, delay: 0.3 }}
                                className="space-y-6"
                            >
                                <div>
                                    <h3 className="text-3xl md:text-4xl font-display font-bold text-white mb-3">
                                        Hey there! I'm <span className="text-transparent bg-clip-text bg-gradient-to-r from-neon-green to-neon-blue">Prithvi</span>.
                                    </h3>
                                    <div className="w-16 h-0.5 bg-gradient-to-r from-neon-green to-transparent mb-4" />
                                </div>
                                
                                <div className="space-y-4 text-gray-300 leading-relaxed">
                                    <p className="text-base md:text-lg">
                                        As a Computer Science student, I'm passionate about crafting intelligent and user-friendly software. I enjoy building delightful user experiences, not just writing functional code.
                                    </p>
                                    <p className="text-base md:text-lg">
                                        When I'm not at my desk, you can find me on the cricket pitch or planning my next trip—activities that provide the fresh perspectives I use to solve complex technical problems.
                                    </p>
                                </div>

                                {/* Quick Stats */}
                                <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-800">
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-neon-green to-neon-blue">3+</div>
                                        <div className="text-xs text-gray-400 mt-1">Years Coding</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-neon-blue to-neon-pink">10+</div>
                                        <div className="text-xs text-gray-400 mt-1">Projects</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-neon-pink to-neon-green">Full</div>
                                        <div className="text-xs text-gray-400 mt-1">Stack Dev</div>
                                    </div>
                                </div>

                                <div className="pt-4">
                                    <motion.a
                                        href="https://drive.google.com/file/d/1GIklfem95lTJyyZPaIBLm-qQp0tMolGG/view?usp=drive_link"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        whileHover={{ scale: 1.05, boxShadow: "0 0 20px rgba(0, 255, 65, 0.4)" }}
                                        whileTap={{ scale: 0.95 }}
                                        className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-neon-green to-neon-blue text-black font-bold rounded-lg transition-all shadow-lg shadow-neon-green/30"
                                    >
                                        <Download size={18} /> View My Resume
                                    </motion.a>
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>
                </motion.div>

                {/* === NEW: Education Section === */}
                <div className="mb-24">
                    <motion.h3
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.5 }}
                        transition={{ duration: 1 }}
                        className="text-3xl font-display font-bold text-white text-center mb-12"
                    >
                        Education
                    </motion.h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                        {education.map((edu, index) => (
                            <motion.div
                                key={index}
                                className="p-6 bg-dark-800/50 border border-gray-700/50 rounded-lg text-center"
                                initial={{ opacity: 0, y: 50 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: index * 0.2 }}
                                whileHover={{ y: -5, boxShadow: `0 0 20px ${edu.color}40` }}
                            >
                                <div className="flex justify-center mb-4">
                                    <div className="p-3 rounded-full bg-black border-2" style={{ borderColor: edu.color }}>
                                        <edu.icon size={28} style={{ color: edu.color }} />
                                    </div>
                                </div>
                                <h4 className="text-xl font-bold text-white mb-1">{edu.degree}</h4>
                                <p className="text-gray-400 text-sm mb-2">{edu.institution}</p>
                                <p className="text-gray-500 text-xs mb-3 font-mono">{edu.years}</p>
                                <span className="px-3 py-1 text-sm font-semibold rounded-full" style={{ backgroundColor: `${edu.color}20`, color: edu.color }}>
                                    {edu.score}
                                </span>
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* Part 3: Academic Journey (Timeline) */}
                <div className="mb-24">
                    <motion.h3
                        initial={{ opacity: 0, y: 50 }}
                        animate={isInView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 1, delay: 0.5 }}
                        className="text-3xl font-display font-bold text-white text-center mb-16"
                    >
                        My Skill Journey
                    </motion.h3>
                    <div className="relative max-w-3xl mx-auto">
                        <motion.div
                            className="absolute left-1/2 top-0 bottom-0 w-0.5 -translate-x-1/2 bg-gradient-to-b from-neon-green via-neon-blue to-neon-pink"
                            initial={{ height: 0 }}
                            animate={isInView ? { height: "100%" } : {}}
                            transition={{ duration: 2, delay: 0.8, ease: "easeOut" }}
                        />
                        {timeline.map((item, index) => (
                            <motion.div
                                key={index}
                                className={`relative flex items-center justify-between w-full mb-16 ${index % 2 === 0 ? 'flex-row-reverse' : ''}`}
                                initial={{ opacity: 0, y: 50 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.5 }}
                                transition={{ duration: 0.8, delay: index * 0.4 }}
                            >
                                <div className="w-5/12">
                                    <h4 className="text-2xl font-display font-bold mb-1" style={{ color: item.color }}>{item.year} - {item.title}</h4>
                                    <p className="text-gray-400">{item.description}</p>
                                </div>
                                <motion.div
                                    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full border-2 flex items-center justify-center bg-black z-10"
                                    style={{ borderColor: item.color }}
                                    whileHover={{ scale: 1.2, rotate: 10 }}
                                >
                                    <item.icon style={{ color: item.color }} size={24} />
                                </motion.div>
                                <div className="w-5/12"></div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}